import { useTranslate } from '../../application/i18n/LocaleProvider';
import { hasAccelerationTrace } from '../../domain/cars/accelerationTrace';
import { hasValueHistory } from '../../domain/cars/carValue';
import CarAccelerationChart from './CarAccelerationChart';
import CarSpecTable from './CarSpecTable';
import CarValueChart from './CarValueChart';

/**
 * THE DATA BLOCK — the bottom half of the stats screen (SPEC-004 PR-4.8).
 *
 * The reference this route is composed to (`The Duel: Test Drive II`, 1989)
 * draws a car in two halves: the art panel with the name printed on it, and
 * under it one bordered box holding the specification in "Label:......value"
 * rows with the acceleration graph beside them. `CarDeckCard` is the first
 * half; this is the second.
 *
 * IT IS ONE BOX, and that is the whole reason this component exists rather than
 * two sections sitting next to each other on the page. The specification and the
 * graph are the same figures seen two ways — the table says the car reached
 * 100 km/h in 4.1 s and the graph says what the next ten seconds looked like —
 * and the reference makes that argument by drawing one frame around both. Two
 * plates with a gap between them is the modern app the screen must not look
 * like.
 *
 * THE HEADINGS STAY `<h2>`, styled as the block's captions rather than as the
 * page's section headings. `.section-heading` carries a heat rule and a
 * --space-8 margin: it is the vocabulary of the long read BELOW the screen, and
 * wearing it here would make the specification announce itself as the start of
 * an article. What a reader gets by ear is unchanged, which is the part that
 * matters — and the acceleration section's ABSENCE is still a section that is
 * not there rather than a heading over blank axes.
 *
 * The block is keyed to the car by its caller, so a deal builds a new one and
 * the boot sequence in `carScreenMotion.css` runs again. Nothing here knows
 * about that: an entrance is attached to the ELEMENT in this repo, and a
 * component that had to be told when to replay one would be a component holding
 * a clock.
 */
export default function CarScreen({ car, cars = [] }) {
    const t = useTranslate();

    return (
        <div className="car-screen">
            {/* THE GRAPH LEADS THE BLOCK, and that is the one place this
                composition departs from the reference's own arrangement rather
                than following it.

                The reference draws the specification and the graph SIDE BY
                SIDE — the graph is a third of the block's width and its full
                height, a peer of the figures rather than a footnote to them.
                A phone has no room for that beside twenty-three rows, so the
                weight has to be bought some other way, and order is the only
                currency a single column has.

                It buys something the reference never needed, too. The trace
                DRAWS ITSELF ON when the screen arrives, and an animation below
                twenty-three rows of specification is an animation nobody is
                looking at: by the time a reader has scrolled to it, it has
                finished. Leading with it is what makes the motion a thing that
                happens to the reader rather than a thing that happened.

                Renders nothing for the cars whose only published road tests are
                of a different variant — and the guard is here as well as inside
                the chart, because this file is where the block's ORDER is
                legible and a panel that can vanish has to say so where a reader
                of this file is looking. */}
            {hasAccelerationTrace(car) ? (
                <section className="car-screen__panel" aria-labelledby="car-acceleration-title">
                    {/* VISUALLY HIDDEN, alone among the block's headings. The
                        plot below prints this exact word inside itself as the
                        reference does, so a caption above it would be the same
                        word twice on one panel, forty pixels apart.

                        It stays in the DOCUMENT because everything that depends
                        on it is invisible: it is what a reader hears, it is what
                        this section is labelled by, and its ABSENCE is how the
                        page says a car has no road test of its own variant. The
                        specification panel keeps its visible caption, and the
                        asymmetry is the argument — one panel labels itself and
                        the other cannot. */}
                    <h2 className="car-screen__caption visually-hidden" id="car-acceleration-title">
                        {t('cars.section.acceleration')}
                    </h2>
                    <CarAccelerationChart car={car} cars={cars} />
                </section>
            ) : null}

            {/* WHAT ONE HAS BEEN WORTH, directly under the graph of how fast it
                is — the two things about a car that are a CURVE rather than a
                figure, and the only two panels in this product drawn as plots.
                They belonged in different halves of the page while the screen
                was one column, because the block was already the tallest thing
                on a phone; side by side with the specification there is a whole
                column free beneath the trace, and a reader comparing what a car
                does with what it costs no longer has to hold one in their head
                while they scroll to the other.

                Renders nothing for the Yellowbird, whose twenty-nine cars leave
                the catalogue with no market average to draw a line to. */}
            {hasValueHistory(car) ? (
                <section className="car-screen__panel car-screen__panel--value" aria-labelledby="car-value-title">
                    <h2 className="car-screen__caption" id="car-value-title">
                        {t('cars.section.value')}
                    </h2>
                    <CarValueChart car={car} />
                </section>
            ) : null}

            {/* And the figures the curve above is the shape of. */}
            <section className="car-screen__panel" aria-labelledby="car-specs-title">
                <h2 className="car-screen__caption" id="car-specs-title">
                    {t('cars.section.specs')}
                </h2>
                <CarSpecTable car={car} />
            </section>
        </div>
    );
}
